import { useSearchParams } from "react-router-dom";
import {
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";

import { formatFitnessType, formatFitnessVatRegistration } from "@/formatter";
import { DataSection } from "@/components/DataSection";
import { MediaViewer } from "@/components/MediaViewer";
import { useRequireParams } from "@/utils";
import defaultLogo from "@/assets/default_fitness_logo.svg";

import { EditFitnessDialog } from "./EditFitnessDialog";
import { FitnessSettingDialog } from "./FitnessSettingDialog";

import type { FitnessCenter } from "@/models";

export type FitnessDetailProps = {
  data: FitnessCenter;
  fetchKeys?: string[];
};

export function FitnessDetail({ data, fetchKeys = [] }: FitnessDetailProps) {
  const { id } = useRequireParams(["id"]);
  const [searchParams, setSearchParams] = useSearchParams();

  function openEditDialog() {
    searchParams.set("dialog", "fitness-edit");
    setSearchParams(searchParams);
  }

  function openSettingDialog() {
    searchParams.set("dialog", "fitness-setting");
    setSearchParams(searchParams);
  }

  function onCloseDialog() {
    searchParams.delete("dialog");
    setSearchParams(searchParams, { replace: true });
  }

  const show = (v?: string | null) => (v && v.trim() ? v : "-");

  const dialog = searchParams.get("dialog");

  const editDialog = {
    open: dialog === "fitness-edit",
    onClose: onCloseDialog,
    data,
    id: +id,
    fetchKeys,
  };

  const settingDialog = {
    open: dialog === "fitness-setting",
    onClose: onCloseDialog,
    data,
    fetchKeys,
  };

  const {
    name,
    logoUrl,
    type,
    isVatRegistration,
    companyName,
    phoneNo,
    mobilePhoneNo,
    faxNo,
    address,
    province,
    postCode,
    district,
    subdistrict,
    taxNo,
    activePlan,
    ownerStaff,
  } = data;

  const columns = [
    [
      {
        title: "ข้อมูลธุรกิจ",
        data: [
          { label: "ประเภทธุรกิจ", value: formatFitnessType(type) },
          {
            label: "จดภาษีมูลค่าเพิ่ม",
            value: formatFitnessVatRegistration(isVatRegistration),
          },
        ],
      },
      {
        title: "ข้อมูลผู้ติดต่อ",
        data: [
          { label: "เบอร์สำนักงาน", value: phoneNo },
          { label: "เบอร์มือถือ", value: mobilePhoneNo },
          { label: "เบอร์โทรสาร", value: faxNo },
          { label: "อีเมลผู้ดูแล", value: show(ownerStaff?.email) },
        ],
      },
    ],
    [
      {
        title: "รายละเอียดธุรกิจ",
        data: [
          { label: "ชื่อธุรกิจ", value: companyName },
          { label: "เลขประจำตัวผู้เสียภาษี", value: taxNo },
          { label: "ที่อยู่", value: address },
          { label: "แขวง / ตำบล", value: subdistrict },
          { label: "เขต / อำเภอ", value: district },
          { label: "จังหวัด", value: province },
          { label: "รหัสไปรษณีย์", value: postCode },
        ],
      },
    ],
  ];

  const fitnessStatus = activePlan?.plan.name ?? "หมดอายุ";

  return (
    <Stack>
      <CardHeader
        title={
          <Stack direction="row" gap={2} alignItems="center">
            <Typography variant="h6">{name}</Typography>
            <Typography variant="body1" color="text.secondary">
              ({fitnessStatus})
            </Typography>
          </Stack>
        }
        action={
          <Stack direction="row">
            <Tooltip title="แก้ไข">
              <IconButton onClick={openEditDialog}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="ตั้งค่า">
              <IconButton onClick={openSettingDialog}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />
      <CardContent sx={{ py: 0 }}>
        <MediaViewer
          mediaList={[{ url: logoUrl ?? defaultLogo }]}
          imageWidth={200}
          imageHeight={200}
        />
      </CardContent>
      <CardContent>
        <DataSection columns={columns} label="" />
      </CardContent>
      <EditFitnessDialog {...editDialog} />
      <FitnessSettingDialog {...settingDialog} />
    </Stack>
  );
}
